import { previewHtmlTypography } from './previewHtmlTypography';
import HtmlInspectorSelect from './HtmlInspectorSelect';
import { getTypographyClasses, getHtmlTypography, getHtmlTypographyTarget, updateElementTypography, updateHtmlTypography } from './htmlTypography';
import { fontOptions, fontWeightOptions } from './PageBuilderConstants';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { usePageBuilder } from './PageBuilderContext';
import { getHtmlTextSelection, updateHtmlTexts } from './htmlTexts';
import styles from './BuilderInspector.module.css';
import BuilderInspectorHeader from './BuilderInspectorHeader';

export default function HtmlTextEditor({ block, view = 'desktop' }) {
    const { selectedHtmlText, updateBlock } = usePageBuilder();
    const html = block.data?.html || '';
    const liveEdit = useRef(null);
    const selection = useMemo(() => {
        if (selectedHtmlText?.blockId !== block.id) return null;
        const key = `${block.id}:${selectedHtmlText.path.join('.')}`;
        if (liveEdit.current?.key === key && liveEdit.current.html === html) return liveEdit.current.selection;
        return getHtmlTextSelection(html, selectedHtmlText.path, true);
    }, [html, block.id, selectedHtmlText]);
    const [values, setValues] = useState([]);
    useEffect(() => setValues(selection?.texts.map(text => text.value) || []), [selection]);
    const elementClass = useMemo(() => getHtmlTypographyTarget(html, selectedHtmlText?.path), [html, selectedHtmlText]);
    const classes = useMemo(() => getTypographyClasses(html, selectedHtmlText?.path).filter(name => name !== elementClass), [html, selectedHtmlText, elementClass]);
    const [extendToClass, setExtendToClass] = useState(false);
    const selectionKey = `${block.id}:${selectedHtmlText?.path?.join('.')}`;
    useEffect(() => setExtendToClass(false), [selectionKey]);
    const [className, setClassName] = useState('');
    const [typography, setTypography] = useState({});
    const [typographyError, setTypographyError] = useState('');
    const [currentTypography, setCurrentTypography] = useState({});
    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            const root = document.getElementById(block.id);
            const element = selectedHtmlText?.path?.reduce((node, index) => node?.childNodes[index], root);
            if (element?.nodeType !== 1) return;
            const computed = getComputedStyle(element);
            setCurrentTypography({ family: computed.fontFamily, size: computed.fontSize, weight: computed.fontWeight });
        });
        return () => cancelAnimationFrame(frame);
    }, [block.id, html, selectedHtmlText, view]);
    useEffect(() => {
        setClassName(previous => classes.includes(previous) ? previous : classes[0] || '');
    }, [classes]);
    const savedTypography = useMemo(() => ({
        ...getHtmlTypography(html, className, view),
        ...getHtmlTypography(html, elementClass, view),
    }), [html, className, elementClass, view]);
    useEffect(() => {
        setTypography(savedTypography);
        setTypographyError('');
    }, [savedTypography, selectedHtmlText]);
    const typographyChanged = ['font-family', 'font-size', 'font-weight'].some(property =>
        (typography[property] || '') !== (savedTypography[property] || ''));
    useLayoutEffect(() => {
        if (!typographyChanged || selectedHtmlText?.blockId !== block.id) return;
        const root = document.getElementById(block.id);
        const element = selectedHtmlText?.path?.reduce((node, index) => node?.childNodes[index], root);
        if (element?.nodeType !== 1) return;
        const targets = extendToClass && className
            ? Array.from(root.getElementsByClassName(className)) : [element];
        return previewHtmlTypography(targets, typography);
    }, [block.id, html, selectedHtmlText, typography, typographyChanged, extendToClass, className, view]);
    if (!selection) return null;
    const applyTypography = allViews => {
        try {
            const withText = html;
            const nextHtml = extendToClass
                ? updateHtmlTypography(withText, selectedHtmlText.path, className, typography, view, allViews, block.id)
                : updateElementTypography(withText, selectedHtmlText.path, typography, view, allViews, block.id);
            updateBlock(block.id, { data: { ...block.data, html: nextHtml } });
            setTypographyError('');
        } catch (error) { setTypographyError(error.message); }
    };
    const hasTypography = Object.values(typography).some(value => value?.trim());


    return <form className={styles.editor} aria-label="Modifica testo HTML" onSubmit={event => event.preventDefault()}>
        <BuilderInspectorHeader type="text" title="Testo selezionato" description="Il testo si aggiorna automaticamente mentre scrivi." badge="TESTO" />
        {selection.texts.length > 1 && <p>Modifica le singole parti del testo mantenendo grassetti, link e altri stili.</p>}
        {selection.texts.map((text, index) => <label key={text.path.join('.')}>
            {selection.texts.length === 1 ? 'Testo' : `Testo ${index + 1}`}
            <textarea rows={Math.min(8, Math.max(2, (values[index] || '').split('\n').length))}
                value={values[index] ?? text.value}
                onKeyDown={event => event.stopPropagation()}
                onChange={event => {
                    const nextValues = selection.texts.map((item, i) => i === index ? event.target.value : values[i] ?? item.value);
                    const texts = selection.texts.map((item, i) => ({ ...item, value: nextValues[i] }));
                    const nextHtml = updateHtmlTexts(html, texts, true);
                    liveEdit.current = { key: selectionKey, html: nextHtml, selection: { ...selection, texts } };
                    setValues(nextValues);
                    updateBlock(block.id, { data: { ...block.data, html: nextHtml } });
                }} />
        </label>)}
        <fieldset>
            <legend className={styles.sectionTitle}>Tipografia · {view === 'desktop' ? 'Desktop' : view === 'tablet' ? 'Tablet' : 'Mobile'}</legend>
            <label className={styles.checkboxLabel}>
                <input type="checkbox" checked={extendToClass} disabled={!classes.length}
                    onChange={event => setExtendToClass(event.target.checked)} />
                Estendi a tutti gli elementi della classe
            </label>
            {extendToClass && <label>Classe CSS
                <HtmlInspectorSelect value={className}
                    options={classes.map(name => ({ id: name, value: name, name: `.${name}` }))}
                    onChange={setClassName} />
            </label>}
            <p>{extendToClass ? 'Le modifiche si applicano a tutti gli elementi con questa classe.' : 'Le modifiche si applicano solo a questo elemento.'} Compila solo le proprietà da cambiare.</p>
            <label>Font
                <HtmlInspectorSelect value={typography['font-family'] || ''}
                    options={[{ id: 'current', value: '', name: `Attuale: ${currentTypography.family || 'ereditato'}` }, ...fontOptions]}
                    onChange={value => setTypography(previous => ({ ...previous, 'font-family': value }))} />
            </label>
            <div className={styles.grid}>
                <label>Dimensione
                    <input placeholder={currentTypography.size || "Es. 24px o 1.5rem"} value={typography['font-size'] || ''}
                        onChange={event => setTypography(previous => ({ ...previous, 'font-size': event.target.value }))} />
                </label>
                <label>Peso
                    <HtmlInspectorSelect value={typography['font-weight'] || ''}
                        options={[{ id: 'current', value: '', name: `Attuale: ${currentTypography.weight || 'ereditato'}` }, ...fontWeightOptions]}
                        onChange={value => setTypography(previous => ({ ...previous, 'font-weight': value }))} />
                </label>
            </div>
            {typographyError && <p role="alert">{typographyError}</p>}
            {typographyChanged && <p>Anteprima delle modifiche non ancora applicate.</p>}
            <div className={styles.typographyActions}>
                <button type="button" disabled={!typographyChanged} onClick={() => { setTypography(savedTypography); setTypographyError(''); }}>Annulla</button>
                <button className={styles.primary} type="button" disabled={!hasTypography} onClick={() => applyTypography(false)}>Applica a {view === 'desktop' ? 'Desktop' : view === 'tablet' ? 'Tablet' : 'Mobile'}</button>
                <button type="button" disabled={!hasTypography} onClick={() => applyTypography(true)}>Applica a tutto</button>
            </div>
        </fieldset>
    </form>;
}
